import BodyText from 'LuxKit/Typography/BodyText'
import { rem } from 'polished'
import React, { useContext } from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'
import TextLink from 'LuxKit/TextLink'
import Image from 'components/Common/Image'
import LinePhoneIcon from 'LuxKit/Icons/line/LinePhoneIcon'
import { mediaQueryUp } from 'components/utils/breakpoint'
import GeoContext from 'contexts/geoContext'
import useUltraLuxLabels from 'hooks/useUltraLuxLabels'
import Group from 'components/utils/Group'
import ContactContext from 'contexts/contactContext'

const USPContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: ${rem(24)};
  padding-top: ${rem(40)};
  border-top: 1px solid ${props => props.theme.palette.neutral.default.five};
  align-items: center;

  ${mediaQueryUp.desktop} {
    display: flex;
    gap: ${rem(40)};
    padding-top: ${rem(24)};
  }
`

const USP = styled(Group)`
  ${mediaQueryUp.tablet} {
    max-width: ${rem(330)};
  }
`

const ImageWrapper = styled.div`
  width: ${rem(68)};
  height: ${rem(68)};
  flex-shrink: 0;

  ${mediaQueryUp.tablet} {
    width: ${rem(84)};
    height: ${rem(84)};
  }
`

interface Props {
  isPrivateRequest?: boolean
  onRequestPrivateTour?: () => void
}

function UltraLuxUSPs(props: Props) {
  const {
    isPrivateRequest,
    onRequestPrivateTour,
  } = props

  const intl = useIntl()
  const { currentRegionCode } = useContext(GeoContext)
  const contact = useContext(ContactContext)
  const ultraluxUSPDefinitions = useUltraLuxLabels()

  const serviceUsp = ultraluxUSPDefinitions.hotels.service(currentRegionCode)

  return (
    <USPContainer>
      <USP
        gap={16}
        tabletGap={20}
        direction="horizontal-reverse"
        tabletDirection="horizontal"
        verticalAlign="start"
        tabletVerticalAlign="center"
        horizontalAlign="space-between"
        tabletHorizontalAlign="start"
      >
        <ImageWrapper>
          <Image fit="center" id={serviceUsp.imageId} width={84} />
        </ImageWrapper>
        <Group direction="vertical" gap={4}>
          <BodyText variant="medium" weight="bold">
            {serviceUsp.title}
          </BodyText>
          <BodyText variant="medium" colour="neutral-three">
            {serviceUsp.subtitle}
          </BodyText>
          <TextLink
            size="medium"
            weight="bold"
            href={`tel:${contact.defaultContact.number}`}
            startIcon={<LinePhoneIcon />}
          >
            {contact.defaultContact.humanReadable}
          </TextLink>
        </Group>
      </USP>
      {isPrivateRequest && (
        <USP
          gap={16}
          tabletGap={20}
          direction="horizontal-reverse"
          tabletDirection="horizontal"
          verticalAlign="start"
          tabletVerticalAlign="center"
          horizontalAlign="space-between"
          tabletHorizontalAlign="start"
        >
          <ImageWrapper>
            <Image fit="center" id="private-request-20230523" />
          </ImageWrapper>
          <Group direction="vertical" gap={4}>
            <BodyText variant="medium" weight="bold" colour="neutral-two">
              {intl.formatMessage({
                id: 'E39H2eT5YmQRXcQJwHHf',
                defaultMessage: 'Make this a private departure',
                description: 'Heading for private departure USP on ultra lux offer page',
              })}
            </BodyText>
            <BodyText variant="medium" colour="neutral-three">
              {intl.formatMessage({
                id: 'JlBThB85n_AXGCbIuY2f',
                defaultMessage: 'Tailor the itinerary to your group’s interests and travel when works for you.',
                description: 'Description for private departure USP on ultra lux offer page',
              })}
            </BodyText>
            <TextLink size="medium" weight="bold" onClick={onRequestPrivateTour}>
              {intl.formatMessage({
                id: 'xA6GJMVdG4-VXgS78Bha',
                defaultMessage: 'Request private departure',
                description: 'Link text to request a private departure on ultra lux offer page',
              })}
            </TextLink>
          </Group>
        </USP>
      )}
    </USPContainer>
  )
}

export default UltraLuxUSPs
